import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import CloseIcon from '@mui/icons-material/Close';
import CloudDownloadOutlinedIcon from '@mui/icons-material/CloudDownloadOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CircularProgress from '@mui/material/CircularProgress';
import AutorenewOutlinedIcon from '@mui/icons-material/AutorenewOutlined';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import { UPCColumnView } from '../../components/common/UPCColumnView';
import { CropActionBar } from '../../components/common/CropActionBar';
import { useMasterLabelEditor } from '../../hooks/useMasterLabelEditor';
import { useNotification } from '../../hooks/useNotification';
import {
  getCachedImageStatus,
  getImageCacheSummary,
  ImageCacheSummary,
  useZoomPreview,
  releaseCachedImages,
} from '../../hooks/useZoomPreview';
import { useAuthContext } from '../../../context/AuthContext';
import { ForgeApiError, ForgeApiService } from '../../services/ForgeApiService';
import { transformApiUPCData } from '../../services/masterLabelTransforms';
import { buildMasterLabelDebugInfo } from '../../services/masterLabelState';
import {
  MasterLabelBackendCropMutation,
  MasterLabelBackendDesignMutation,
  MasterLabelDebugInfo,
  MasterLabelSaveSummary,
  MasterUPCData,
} from '../../types';

const CHAIN_OPTIONS = [
  { id: 24, label: 'Village' },
  { id: 26, label: 'Waitrose' },
  { id: 30, label: 'Morrisons' },
  { id: 41, label: 'Food Parade' },
  { id: 44, label: 'Collins Family Markets' },
  { id: 46, label: 'Lidl GB' },
  { id: 50, label: 'Lidl FR' },
] as const;
const DEFAULT_CHAIN_ID = 46;
const AUTO_RELOAD_STORAGE_KEY = 'forge-auto-reload-images';
const SAVE_REVIEW_STORAGE_KEY = 'forge-show-save-review';
const TEXT_SCALE_STORAGE_KEY = 'forge-text-scale';

const EMPTY_IMAGE_CACHE_SUMMARY: ImageCacheSummary = {
  total: 0,
  loaded: 0,
  pending: 0,
  failed: 0,
  idle: 0,
};

const HIGHLIGHT_PRESETS = {
  similar: {
    color: '#58a6ff',
    glow: '0 0 12px rgba(88,166,255,0.58), 0 0 24px rgba(88,166,255,0.24)',
    message: 'Highlighted crops are very similar.',
  },
  different: {
    color: '#f85149',
    glow: '0 0 12px rgba(248,81,73,0.58), 0 0 24px rgba(248,81,73,0.24)',
    message: 'Highlighted crops should not be in the same design.',
  },
  bad_quality: {
    color: '#e3b341',
    glow: '0 0 12px rgba(227,179,65,0.58), 0 0 24px rgba(227,179,65,0.24)',
    message: 'Highlighted crops may have bad quality.',
  },
} as const;

type HighlightPresetKey = keyof typeof HIGHLIGHT_PRESETS;
type PendingColumnState = {
  label: string;
  status: 'loading';
};

type SaveReviewData = {
  debugInfo: MasterLabelDebugInfo;
  backendSummary?: MasterLabelSaveSummary;
  backendItemCount?: number;
  backendCropMutations?: MasterLabelBackendCropMutation[];
  backendDesignMutations?: MasterLabelBackendDesignMutation[];
  error?: {
    message: string;
    status?: number;
    detail?: string;
    rawBody?: string;
  } | null;
};

const formatMutationValue = (value: string | boolean | null | undefined): string => {
  if (value === null || value === undefined) return 'none';
  if (value === '') return "''";
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  return String(value);
};

const describeBackendCropMutation = (mutation: MasterLabelBackendCropMutation): string[] => (
  mutation.changedFields.map((field) => {
    switch (field) {
      case 'source':
        return `source: ${formatMutationValue(mutation.baseSource)} -> ${formatMutationValue(mutation.nextSource)}`;
      case 'barcode':
        return `barcode: ${formatMutationValue(mutation.baseBarcode)} -> ${formatMutationValue(mutation.nextBarcode)}`;
      case 'item_number':
        return `item_number: ${formatMutationValue(mutation.baseItemNumber)} -> ${formatMutationValue(mutation.nextItemNumber)}`;
      case 'normalized_upc':
        return `normalized_upc: ${formatMutationValue(mutation.baseNormalizedUpc)} -> ${formatMutationValue(mutation.nextNormalizedUpc)}`;
      case 'design_id':
        return `design_id: ${formatMutationValue(mutation.baseDesignId)} -> ${formatMutationValue(mutation.nextDesignId)}`;
      case 'orientation':
        return `orientation: ${formatMutationValue(mutation.baseOrientation)} -> ${formatMutationValue(mutation.nextOrientation)}`;
      case 'classification_type':
        return `classification_type: ${formatMutationValue(mutation.baseClassificationType)} -> ${formatMutationValue(mutation.nextClassificationType)}`;
      case 'is_active':
        return `is_active: ${formatMutationValue(mutation.baseIsActive)} -> ${formatMutationValue(mutation.nextIsActive)}`;
      case 'is_pending':
        return `is_pending: ${formatMutationValue(mutation.baseIsPending)} -> ${formatMutationValue(mutation.nextIsPending)}`;
      case 'pending_reason':
        return `pending_reason: ${formatMutationValue(mutation.basePendingReason)} -> ${formatMutationValue(mutation.nextPendingReason)}`;
      default:
        return field;
    }
  })
);

const buildSaveErrorData = (error: unknown): NonNullable<SaveReviewData['error']> => {
  if (error instanceof ForgeApiError) {
    return {
      message: error.message,
      status: error.status,
      detail: error.detail,
      rawBody: error.rawBody,
    };
  }

  if (error instanceof Error) {
    if (error.message === 'Failed to fetch') {
      return {
        message: 'Failed to fetch',
        detail: 'The browser did not receive an HTTP response. This usually means the backend crashed, the request was blocked by CORS, or the API is unreachable. Check the backend logs.',
      };
    }
    return {
      message: error.message,
    };
  }

  return {
    message: 'Save failed',
  };
};

const SaveReviewDialog: React.FC<{
  data: SaveReviewData;
  enabled: boolean;
  onEnabledChange: (enabled: boolean) => void;
  onClose: () => void;
}> = ({ data, enabled, onEnabledChange, onClose }) => {
  const backendCropMutations = data.backendCropMutations ?? [];
  const backendDesignMutations = data.backendDesignMutations ?? [];
  const backendRecordChangeTotal = backendCropMutations.length > 0 || backendDesignMutations.length > 0
    ? backendCropMutations.length + backendDesignMutations.length
    : (data.backendSummary?.cropChanges ?? 0)
      + (data.backendSummary?.representativeChanges ?? 0)
      + (data.backendSummary?.designsCreated ?? 0);
  const backendFieldMutationTotal = backendCropMutations.length > 0 || backendDesignMutations.length > 0
    ? backendCropMutations.reduce((total, mutation) => total + mutation.changedFields.length, 0) + backendDesignMutations.length
    : backendRecordChangeTotal;

  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        onClose();
      }
    };
    window.addEventListener('keydown', handler, true);
    return () => window.removeEventListener('keydown', handler, true);
  }, [onClose]);

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.66)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1100,
        padding: 20,
      }}
      onClick={onClose}
    >
      <div
        onClick={(event) => event.stopPropagation()}
        style={{
          width: 'min(1080px, 96vw)',
          maxHeight: '88vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius)',
          boxShadow: 'var(--shadow-elevated)',
        }}
      >
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          padding: '14px 18px',
          borderBottom: '1px solid var(--border)',
          background: 'var(--table-header-bg)',
        }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>
              Master Label Save Review
            </div>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-tertiary)' }}>
              FE logical changes: {data.debugInfo.summary.totalChanges}
              {data.backendSummary ? ` · Backend records changed: ${backendRecordChangeTotal} · Backend field mutations: ${backendFieldMutationTotal}` : ''}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              fontFamily: 'var(--font-mono)',
              fontSize: 10,
              color: 'var(--text-secondary)',
              cursor: 'pointer',
              userSelect: 'none',
            }}>
              <input
                type="checkbox"
                className="forge-checkbox"
                checked={enabled}
                onChange={(event) => onEnabledChange(event.target.checked)}
              />
              Open after each save
            </label>
            <CloseIcon onClick={onClose} style={{ fontSize: 18, cursor: 'pointer', color: 'var(--text-tertiary)' }} />
          </div>
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1.4fr) minmax(320px, 0.9fr)',
          gap: 0,
          minHeight: 0,
          flex: 1,
        }}>
          <div style={{ overflowY: 'auto', padding: 18, display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{
              padding: 12,
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-sm)',
              background: 'var(--bg)',
            }}>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 8 }}>
                FE Summary
              </div>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--text-secondary)', lineHeight: 1.7 }}>
                <div>Total logical changes: {data.debugInfo.summary.totalChanges}</div>
                <div>Changed items: {data.debugInfo.summary.dirtyItemNumbers.join(', ') || 'none'}</div>
                {data.debugInfo.summary.itemSummaries.map((itemSummary) => (
                  <div key={itemSummary.itemNumber}>
                    {itemSummary.itemNumber}: {itemSummary.changeCount} total
                    {' · '}
                    {itemSummary.cropChangeCount} crop placements
                    {' · '}
                    {itemSummary.representativeChangeCount} representative updates
                    {' · '}
                    {itemSummary.draftDesignCount} draft designs
                  </div>
                ))}
              </div>
            </div>

            <div style={{
              padding: 12,
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-sm)',
              background: 'var(--bg)',
            }}>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 8 }}>
                FE Crop Placement Changes
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {data.debugInfo.cropChanges.length > 0 ? data.debugInfo.cropChanges.map((change) => (
                  <div key={change.cropId} style={{
                    fontFamily: 'var(--font-mono)',
                    fontSize: 10,
                    color: 'var(--text-secondary)',
                    lineHeight: 1.5,
                    paddingBottom: 6,
                    borderBottom: '1px solid rgba(255,255,255,0.04)',
                  }}>
                    <div style={{ color: 'var(--text-primary)' }}>{change.cropId}</div>
                    <div>
                      {change.baseItemNumber || 'none'} / {change.baseSection || 'none'} / {change.baseDesignId || 'no-design'}
                      {' -> '}
                      {change.nextItemNumber || 'none'} / {change.nextSection || 'none'} / {change.nextDesignId || 'no-design'}
                    </div>
                  </div>
                )) : (
                  <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--text-tertiary)' }}>
                    No crop placement changes.
                  </div>
                )}
              </div>
            </div>

            <div style={{
              padding: 12,
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-sm)',
              background: 'var(--bg)',
            }}>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 8 }}>
                FE Design Changes
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {data.debugInfo.representativeChanges.map((change) => (
                  <div key={change.designId} style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--text-secondary)' }}>
                    Representative {change.designId}: {change.baseRepresentativeCropId || 'none'} {' -> '} {change.nextRepresentativeCropId || 'none'}
                  </div>
                ))}
                {data.debugInfo.draftDesigns.map((design) => (
                  <div key={design.designId} style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--text-secondary)' }}>
                    Create {design.designId} in {design.itemNumber} with rep {design.representativeCropId || 'none'} and crops {design.cropIds.join(', ')}
                  </div>
                ))}
                {data.debugInfo.representativeChanges.length === 0 && data.debugInfo.draftDesigns.length === 0 && (
                  <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--text-tertiary)' }}>
                    No representative or design-creation changes.
                  </div>
                )}
              </div>
            </div>
          </div>

          <div style={{
            overflowY: 'auto',
            padding: 18,
            borderLeft: '1px solid var(--border)',
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
            background: 'rgba(255,255,255,0.01)',
          }}>
            <div style={{
              padding: 12,
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-sm)',
              background: 'var(--bg)',
            }}>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 8 }}>
                Backend Summary
              </div>
              {data.backendSummary ? (
                <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--text-secondary)', lineHeight: 1.7 }}>
                  <div>Items saved: {data.backendSummary.itemsSaved}</div>
                  <div>Changed records: {backendRecordChangeTotal}</div>
                  <div>Field mutations: {backendFieldMutationTotal}</div>
                  <div>Crop mutations: {data.backendSummary.cropChanges}</div>
                  <div>Representative mutations: {data.backendSummary.representativeChanges}</div>
                  <div>Designs created: {data.backendSummary.designsCreated}</div>
                  <div>Saved item payload returned: {data.backendItemCount ?? 0}</div>
                </div>
              ) : (
                <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--text-tertiary)' }}>
                  No backend summary available because the save did not complete.
                </div>
              )}
            </div>

            <div style={{
              padding: 12,
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-sm)',
              background: 'var(--bg)',
            }}>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 8 }}>
                Backend Crop Mutations
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {backendCropMutations.length > 0 ? backendCropMutations.map((mutation) => (
                  <div key={mutation.cropId} style={{
                    fontFamily: 'var(--font-mono)',
                    fontSize: 10,
                    color: 'var(--text-secondary)',
                    lineHeight: 1.5,
                    paddingBottom: 6,
                    borderBottom: '1px solid rgba(255,255,255,0.04)',
                  }}>
                    <div style={{ color: 'var(--text-primary)' }}>{mutation.cropId}</div>
                    <div>Fields: {mutation.changedFields.join(', ')}</div>
                    {describeBackendCropMutation(mutation).map((line) => (
                      <div key={`${mutation.cropId}-${line}`}>{line}</div>
                    ))}
                  </div>
                )) : (
                  <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--text-tertiary)' }}>
                    No backend crop mutation details returned.
                  </div>
                )}
              </div>
            </div>

            <div style={{
              padding: 12,
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-sm)',
              background: 'var(--bg)',
            }}>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 8 }}>
                Backend Design Mutations
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {backendDesignMutations.length > 0 ? backendDesignMutations.map((mutation) => (
                  <div key={`${mutation.mutationType}-${mutation.designId}`} style={{
                    fontFamily: 'var(--font-mono)',
                    fontSize: 10,
                    color: 'var(--text-secondary)',
                    lineHeight: 1.5,
                    paddingBottom: 6,
                    borderBottom: '1px solid rgba(255,255,255,0.04)',
                  }}>
                    <div style={{ color: 'var(--text-primary)' }}>{mutation.designId}</div>
                    <div>Type: {mutation.mutationType}</div>
                    <div>
                      Representative: {formatMutationValue(mutation.baseRepresentativeCropId)}
                      {' -> '}
                      {formatMutationValue(mutation.nextRepresentativeCropId)}
                    </div>
                  </div>
                )) : (
                  <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--text-tertiary)' }}>
                    No backend design mutation details returned.
                  </div>
                )}
              </div>
            </div>

            <div style={{
              padding: 12,
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-sm)',
              background: 'var(--bg)',
            }}>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 8 }}>
                Why FE and BE counts can differ
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                FE counts logical UI actions. Backend `cropChanges` counts crop documents whose persisted fields changed during save.
                Those fields include `normalized_upc`, `item_number`, `design_id`, `orientation`, `classification_type`, `is_active`, `is_pending`, and pending-state flags.
              </div>
              {data.backendSummary && backendRecordChangeTotal !== data.debugInfo.summary.totalChanges && (
                <div style={{ marginTop: 10, fontSize: 11, color: 'var(--warning, #e3b341)', lineHeight: 1.6 }}>
                  This save diverged: FE saw {data.debugInfo.summary.totalChanges} logical changes, while backend changed {backendRecordChangeTotal} records and {backendFieldMutationTotal} persisted fields.
                  That usually means one UI action rewrote multiple persisted fields on the same crop or design.
                </div>
              )}
            </div>

            {data.error && (
              <div style={{
                padding: 12,
                border: '1px solid rgba(248,81,73,0.35)',
                borderRadius: 'var(--radius-sm)',
                background: 'rgba(248,81,73,0.08)',
              }}>
                <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 700, color: 'var(--red)' }}>
                  Save Error
                </div>
                <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6, fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--text-secondary)' }}>
                  <div>{data.error.message}</div>
                  {data.error.status !== undefined && (
                    <div>Status: {data.error.status}</div>
                  )}
                  {data.error.detail && data.error.detail !== data.error.message && (
                    <div>Detail: {data.error.detail}</div>
                  )}
                  {data.error.rawBody && data.error.rawBody !== data.error.detail && (
                    <details>
                      <summary style={{ cursor: 'pointer' }}>Raw backend error body</summary>
                      <pre style={{
                        marginTop: 8,
                        fontSize: 10,
                        lineHeight: 1.5,
                        color: 'var(--text-secondary)',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                      }}>
                        {data.error.rawBody}
                      </pre>
                    </details>
                  )}
                </div>
              </div>
            )}

            <details style={{
              padding: 12,
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-sm)',
              background: 'var(--bg)',
            }}>
              <summary style={{ cursor: 'pointer', fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 700, color: 'var(--text-primary)' }}>
                Raw save request
              </summary>
              <pre style={{
                marginTop: 10,
                fontSize: 10,
                lineHeight: 1.5,
                color: 'var(--text-secondary)',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}>
                {JSON.stringify(data.debugInfo.request, null, 2)}
              </pre>
            </details>
          </div>
        </div>
      </div>
    </div>
  );
};

export const MasterLabelPage: React.FC = () => {
  const { token, hasInternalAccess } = useAuthContext();

  // URL state
  const [searchParams, setSearchParams] = useSearchParams();

  // Chain state (from query param, default 46)
  const [selectedChainId, setSelectedChainId] = useState<number>(() => {
    const param = searchParams.get('chain');
    const parsed = param ? Number(param) : NaN;
    return CHAIN_OPTIONS.some(c => c.id === parsed) ? parsed : DEFAULT_CHAIN_ID;
  });

  // Item entry state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchFocused, setSearchFocused] = useState(false);

  // Loaded item-number columns
  const [selectedItemNumbers, setSelectedItemNumbers] = useState<string[]>([]);
  const [pendingColumn, setPendingColumn] = useState<PendingColumnState | null>(null);
  const deepLinkHandled = useRef(false);
  const [deepLinkLoading, setDeepLinkLoading] = useState(false);

  // Save state
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [showSaveReview, setShowSaveReview] = useState<boolean>(() => {
    try { return localStorage.getItem(SAVE_REVIEW_STORAGE_KEY) !== 'false'; }
    catch { return true; }
  });
  const [saveReviewData, setSaveReviewData] = useState<SaveReviewData | null>(null);
  const [autoReloadImages, setAutoReloadImages] = useState<boolean>(() => {
    try { return localStorage.getItem(AUTO_RELOAD_STORAGE_KEY) === 'true'; }
    catch { return false; }
  });
  const [imageCacheSummary, setImageCacheSummary] = useState<ImageCacheSummary>(
    EMPTY_IMAGE_CACHE_SUMMARY
  );
  const [isLoadingRemainingImages, setIsLoadingRemainingImages] = useState(false);
  const [reloadVersion, setReloadVersion] = useState(0);


  const [cropSize, setCropSize] = useState<number>(() => {
    try {
      const v = localStorage.getItem('forge-crop-size');
      const parsed = v ? Number(v) : NaN;
      return Number.isFinite(parsed) ? parsed : 100;
    }
    catch { return 100; }
  });
  const [textScale, setTextScale] = useState<number>(() => {
    try {
      const v = localStorage.getItem(TEXT_SCALE_STORAGE_KEY);
      const parsed = v ? Number(v) : NaN;
      return Number.isFinite(parsed) ? parsed : 100;
    }
    catch { return 100; }
  });
  const contentRef = useRef<HTMLDivElement>(null);
  const hasScrolledToHighlightRef = useRef(false);

  const { notify, NotificationToast } = useNotification();

  const editor = useMasterLabelEditor({ notify });
  const isSubmittingQuery = pendingColumn !== null;

  const { handleImageHover, handleImageMove, handleImageLeave, ZoomOverlay, preloadImages, zoomEnabled, toggleZoom } = useZoomPreview();
  const highlightCropIds = React.useMemo(
    () => {
      const nextIds = searchParams.getAll('c')
        .map((value) => value.trim())
        .filter(Boolean);
      return new Set(nextIds);
    },
    [searchParams]
  );
  const highlightPresetKey = searchParams.get('m') as HighlightPresetKey | null;
  const highlightPreset = highlightPresetKey && highlightPresetKey in HIGHLIGHT_PRESETS
    ? HIGHLIGHT_PRESETS[highlightPresetKey]
    : null;

  // Persist crop size and section style to localStorage
  useEffect(() => {
    try { localStorage.setItem('forge-crop-size', String(cropSize)); }
    catch { /* ignore */ }
  }, [cropSize]);

  useEffect(() => {
    try { localStorage.setItem(TEXT_SCALE_STORAGE_KEY, String(textScale)); }
    catch { /* ignore */ }
  }, [textScale]);

  useEffect(() => {
    try { localStorage.setItem(AUTO_RELOAD_STORAGE_KEY, String(autoReloadImages)); }
    catch { /* ignore */ }
  }, [autoReloadImages]);

  useEffect(() => {
    try { localStorage.setItem(SAVE_REVIEW_STORAGE_KEY, String(showSaveReview)); }
    catch { /* ignore */ }
  }, [showSaveReview]);

  // Preload all crop images for instant zoom
  const preloadUpcImages = useCallback((data: MasterUPCData) => {
    const urls: string[] = [];
    for (const v of data.designs) for (const c of v.crops) urls.push(c.imageUrl);
    if (data.pendingCrops) for (const c of data.pendingCrops) urls.push(c.imageUrl);
    for (const c of data.unassignedCrops) urls.push(c.imageUrl);
    for (const c of data.nonFrontCrops) urls.push(c.imageUrl);
    for (const c of data.cartonCrops) urls.push(c.imageUrl);
    if (data.inactiveCrops) for (const c of data.inactiveCrops) urls.push(c.imageUrl);
    preloadImages(urls);
  }, [preloadImages]);

  const allCropImageUrls = React.useMemo(() => {
    const urls: string[] = [];
    for (const upc of editor.upcData) {
      for (const v of upc.designs) for (const c of v.crops) urls.push(c.imageUrl);
      if (upc.pendingCrops) for (const c of upc.pendingCrops) urls.push(c.imageUrl);
      for (const c of upc.unassignedCrops) urls.push(c.imageUrl);
      for (const c of upc.nonFrontCrops) urls.push(c.imageUrl);
      for (const c of upc.cartonCrops) urls.push(c.imageUrl);
      if (upc.inactiveCrops) for (const c of upc.inactiveCrops) urls.push(c.imageUrl);
    }
    return [...new Set(urls)];
  }, [editor.upcData]);

  const refreshImageCacheSummary = useCallback(() => {
    setImageCacheSummary(getImageCacheSummary(allCropImageUrls));
  }, [allCropImageUrls]);

  useEffect(() => {
    refreshImageCacheSummary();
    if (allCropImageUrls.length === 0) return;
    const interval = setInterval(refreshImageCacheSummary, 1500);
    return () => clearInterval(interval);
  }, [allCropImageUrls.length, refreshImageCacheSummary]);

  const loadRemainingImages = useCallback((forceRetry: boolean) => {
    const remainingUrls = allCropImageUrls.filter((url) => {
      const status = getCachedImageStatus(url);
      return status === 'idle' || status === 'failed';
    });

    if (remainingUrls.length === 0) {
      refreshImageCacheSummary();
      return 0;
    }

    preloadImages(remainingUrls, {
      forceRetry,
      batchSize: 2,
      delayMs: 250,
    });
    refreshImageCacheSummary();
    return remainingUrls.length;
  }, [allCropImageUrls, preloadImages, refreshImageCacheSummary]);

  const handleLoadRemainingImages = useCallback(() => {
    const count = loadRemainingImages(true);
    setIsLoadingRemainingImages(count > 0);
    if (count > 0) {
      setReloadVersion((prev) => prev + 1);
      notify(`Retrying ${count} remaining crop image${count !== 1 ? 's' : ''}`);
    } else {
      notify('No remaining crop images to reload');
    }
    setTimeout(() => setIsLoadingRemainingImages(false), 1500);
  }, [loadRemainingImages, notify]);

  useEffect(() => {
    if (!autoReloadImages || allCropImageUrls.length === 0) return;
    const kick = () => {
      const count = loadRemainingImages(true);
      if (count > 0) {
        setReloadVersion((prev) => prev + 1);
        setIsLoadingRemainingImages(true);
        setTimeout(() => setIsLoadingRemainingImages(false), 1500);
      }
    };
    kick();
    const interval = setInterval(kick, 8000);
    return () => clearInterval(interval);
  }, [autoReloadImages, allCropImageUrls.length, loadRemainingImages]);

  const syncUrlParams = useCallback((chainId: number, itemNumbers: string[]) => {
    const params = new URLSearchParams();
    if (chainId !== DEFAULT_CHAIN_ID) params.set('chain', String(chainId));
    itemNumbers.forEach((itemNumber) => params.append('in', itemNumber));
    const highlightIds = searchParams.getAll('c')
      .map((value) => value.trim())
      .filter(Boolean);
    const highlightMode = searchParams.get('m');
    [...new Set(highlightIds)].forEach((cropId) => params.append('c', cropId));
    if (highlightMode) params.set('m', highlightMode);
    setSearchParams(params, { replace: true });
  }, [searchParams, setSearchParams]);

  // Reset loaded items when chain changes
  const prevChainId = useRef(selectedChainId);
  useEffect(() => {
    if (prevChainId.current === selectedChainId) return;
    prevChainId.current = selectedChainId;
    setSelectedItemNumbers([]);
    setSearchQuery('');
    editor.replaceAllUpcs([]);
    setSaveError(null);
    setSaveReviewData(null);
    setImageCacheSummary(EMPTY_IMAGE_CACHE_SUMMARY);
    syncUrlParams(selectedChainId, []);
  }, [selectedChainId, editor, syncUrlParams]);

  // Handle deep links: ?in=...
  useEffect(() => {
    if (deepLinkHandled.current || !token) return;
    const itemNumberParams = [...new Set(
      searchParams.getAll('in').map((value) => value.trim()).filter(Boolean)
    )];
    if (itemNumberParams.length === 0) return;
    deepLinkHandled.current = true;
    setDeepLinkLoading(true);

    Promise.allSettled(
      itemNumberParams.map(async (itemNumber) => {
        const apiData = await ForgeApiService.fetchItem(token, itemNumber, selectedChainId);
        return { itemNumber, data: transformApiUPCData(apiData) };
      })
    )
      .then((results) => {
        const loaded = new Map<string, MasterUPCData>();
        const failedItems: string[] = [];

        results.forEach((result, index) => {
          if (result.status === 'fulfilled') {
            loaded.set(result.value.itemNumber, result.value.data);
          } else {
            failedItems.push(itemNumberParams[index]);
          }
        });

        if (loaded.size > 0) {
          const loadedItemNumbers = [...loaded.keys()];
          const loadedItems = [...loaded.values()];
          setSelectedItemNumbers(loadedItemNumbers);
          syncUrlParams(selectedChainId, loadedItemNumbers);
          editor.replaceAllUpcs(loadedItems);
          loadedItems.forEach(preloadUpcImages);
        }

        if (failedItems.length > 0) {
          notify(
            failedItems.length === itemNumberParams.length
              ? 'Failed to load item(s) from link'
              : `Some item(s) from link could not be loaded: ${failedItems.join(', ')}`,
            'warning'
          );
        }
      })
      .finally(() => setDeepLinkLoading(false));
  }, [searchParams, editor, token, selectedChainId, notify, preloadUpcImages, syncUrlParams]);

  useEffect(() => {
    if (highlightCropIds.size === 0) return;
    hasScrolledToHighlightRef.current = false;
  }, [searchParams, highlightCropIds.size]);

  useEffect(() => {
    if (highlightCropIds.size === 0 || hasScrolledToHighlightRef.current || editor.upcData.length === 0) return;
    const firstVisibleHighlight = [...highlightCropIds].find((cropId) => (
      editor.upcData.some((upc) => (
        upc.designs.some((design) => design.crops.some((crop) => crop.id === cropId))
        || (upc.pendingCrops ?? []).some((crop) => crop.id === cropId)
        || upc.unassignedCrops.some((crop) => crop.id === cropId)
        || upc.nonFrontCrops.some((crop) => crop.id === cropId)
        || upc.cartonCrops.some((crop) => crop.id === cropId)
        || (upc.inactiveCrops ?? []).some((crop) => crop.id === cropId)
      ))
    ));
    if (!firstVisibleHighlight) return;

    const frame = window.requestAnimationFrame(() => {
      const target = contentRef.current?.querySelector<HTMLElement>(`[data-crop-id="${CSS.escape(firstVisibleHighlight)}"]`);
      if (target) {
        target.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
        hasScrolledToHighlightRef.current = true;
      }
    });
    return () => window.cancelAnimationFrame(frame);
  }, [editor.upcData, highlightCropIds]);

  // Check if any selected crop has source === 'scan' (non-internal users cannot deactivate these)
  const selectedHasScanSource = React.useMemo(() => {
    if (hasInternalAccess || editor.selectedCropIds.size === 0) return false;
    const ids = editor.selectedCropIds;
    for (const upc of editor.upcData) {
      const allCrops = [
        ...upc.designs.flatMap(d => d.crops),
        ...(upc.pendingCrops ?? []),
        ...upc.unassignedCrops,
        ...upc.nonFrontCrops,
        ...upc.cartonCrops,
        ...(upc.inactiveCrops ?? []),
      ];
      if (allCrops.some(crop => ids.has(crop.id) && crop.source === 'scan')) return true;
    }
    return false;
  }, [hasInternalAccess, editor.selectedCropIds, editor.upcData]);

  const changeCount = editor.diffSummary.totalChanges;

  const newDesignOptions = React.useMemo(() => {
    if (!editor.selectedCropItemNumber) return [];
    return [{
      itemNumber: editor.selectedCropItemNumber,
      label: 'New Design',
    }];
  }, [editor.selectedCropItemNumber]);

  const loadItem = useCallback(async (itemNumber: string, label: string) => {
    if (!token) return;
    if (selectedItemNumbers.includes(itemNumber)) {
      notify(`Item already loaded: ${label || itemNumber}`, 'warning');
      return;
    }
    setPendingColumn({ label, status: 'loading' });
    try {
      const apiData = await ForgeApiService.fetchItem(token, itemNumber, selectedChainId);
      const data = transformApiUPCData(apiData);
      const updated = [...selectedItemNumbers, data.itemNumber];
      setSelectedItemNumbers(updated);
      syncUrlParams(selectedChainId, updated);
      editor.appendLoadedUpc(data);
      preloadUpcImages(data);
      setSearchQuery('');
      setSearchFocused(false);
    } catch {
      notify(`Failed to load item: ${itemNumber}`, 'error');
    } finally {
      setPendingColumn(null);
    }
  }, [token, selectedChainId, selectedItemNumbers, editor, notify, syncUrlParams, preloadUpcImages]);

  // Check if an item has pending (unsaved) changes
  const upcHasChanges = useCallback((itemNumber: string): boolean => {
    return editor.upcHasChanges(itemNumber);
  }, [editor]);

  // Remove item column
  const removeUPC = useCallback((itemNumber: string) => {
    if (upcHasChanges(itemNumber) && !window.confirm(`Item "${itemNumber}" has unsaved changes. Remove anyway?`)) return;
    const upcData = editor.upcData.find(d => d.itemNumber === itemNumber);
    if (upcData) {
      const urls: string[] = [];
      for (const v of upcData.designs) for (const c of v.crops) urls.push(c.imageUrl);
      if (upcData.pendingCrops) for (const c of upcData.pendingCrops) urls.push(c.imageUrl);
      for (const c of upcData.unassignedCrops) urls.push(c.imageUrl);
      for (const c of upcData.nonFrontCrops) urls.push(c.imageUrl);
      for (const c of upcData.cartonCrops) urls.push(c.imageUrl);
      if (upcData.inactiveCrops) for (const c of upcData.inactiveCrops) urls.push(c.imageUrl);
      releaseCachedImages(urls);
    }
    const updated = selectedItemNumbers.filter((value) => value !== itemNumber);
    setSelectedItemNumbers(updated);
    syncUrlParams(selectedChainId, updated);
    editor.removeUpc(itemNumber);
  }, [editor, selectedItemNumbers, selectedChainId, syncUrlParams, upcHasChanges]);

  // Load the typed item number directly on Enter.
  const handleSearchKeyDown = useCallback(async (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (isSubmittingQuery) return;
      const itemNumber = searchQuery.trim();
      if (!itemNumber) return;
      await loadItem(itemNumber, itemNumber);
    }
  }, [isSubmittingQuery, loadItem, searchQuery]);

  // Keyboard shortcuts
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      // Don't trigger when typing in inputs
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;

      if (e.key === 'Escape') {
        editor.setSelectedCropIds(new Set());
      }
      if ((e.metaKey || e.ctrlKey) && e.key === 'z') {
        e.preventDefault();
        editor.undo();
      }
      // Shortcuts when crops are selected
      if (editor.selectedCropIds.size > 0) {
        if (e.key === 'd' || e.key === 'D') {
          if (editor.selectedCropItemNumber) editor.createNewDesign(editor.selectedCropItemNumber);
        }
        if (e.key === 'c' || e.key === 'C') {
          if (editor.selectedCropItemNumber) editor.handleSectionClick({ itemNumber: editor.selectedCropItemNumber, type: 'carton' });
        }
        if (e.key === 'n' || e.key === 'N') {
          if (editor.selectedCropItemNumber) editor.handleSectionClick({ itemNumber: editor.selectedCropItemNumber, type: 'non-front' });
        }
        if (e.key === 'i' || e.key === 'I') {
          if (selectedHasScanSource) { notify('Cannot deactivate scanned crops', 'warning'); return; }
          if (editor.selectedCropItemNumber) editor.handleSectionClick({ itemNumber: editor.selectedCropItemNumber, type: 'inactive' });
        }
        if (e.key === 'r' || e.key === 'R') {
          const rep = editor.selectedCropDesignInfo;
          if (rep && !rep.isAlreadyRep) editor.setRepresentative(rep.designId, rep.cropId);
        }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [editor, notify, selectedHasScanSource]);

  const handleSubmit = useCallback(async () => {
    if (!token || isSaving || changeCount === 0) return;
    setIsSaving(true);
    setSaveError(null);
    let debugGroupOpen = false;
    let debugInfo: MasterLabelDebugInfo | null = null;
    try {
      debugInfo = buildMasterLabelDebugInfo(selectedChainId, editor.baseUpcData, editor.upcData);
      const request = debugInfo.request;
      console.groupCollapsed(
        `[Forge][Master Label] Save debug: ${debugInfo.summary.totalChanges} FE changes across ${debugInfo.summary.dirtyItemNumbers.length} item(s)`
      );
      debugGroupOpen = true;
      console.log('FE diff summary', debugInfo.summary);
      console.log('Save request', request);
      console.table(debugInfo.summary.itemSummaries);
      if (debugInfo.cropChanges.length > 0) {
        console.log('Crop placement changes', debugInfo.cropChanges);
        console.table(debugInfo.cropChanges);
      }
      if (debugInfo.representativeChanges.length > 0) {
        console.log('Representative changes', debugInfo.representativeChanges);
        console.table(debugInfo.representativeChanges);
      }
      if (debugInfo.draftDesigns.length > 0) {
        console.log('Draft designs to create', debugInfo.draftDesigns);
        console.table(debugInfo.draftDesigns);
      }
      const response = await ForgeApiService.saveState(token, request);
      console.log('Backend save summary', response.summary);
      if ((response.cropMutations ?? []).length > 0) {
        console.log('Backend crop mutations', response.cropMutations);
        console.table(response.cropMutations);
      }
      if ((response.designMutations ?? []).length > 0) {
        console.log('Backend design mutations', response.designMutations);
        console.table(response.designMutations);
      }
      console.log('Backend saved item count', (response.items ?? response.upcs ?? []).length);
      console.groupEnd();
      debugGroupOpen = false;
      if (showSaveReview) {
        setSaveReviewData({
          debugInfo,
          backendSummary: response.summary,
          backendItemCount: (response.items ?? response.upcs ?? []).length,
          backendCropMutations: response.cropMutations,
          backendDesignMutations: response.designMutations,
        });
      }
      const freshPayload = response.items ?? response.upcs ?? [];
      const freshData = freshPayload.map(transformApiUPCData);
      editor.rebaseUpcs(freshData);
      const savedCount = debugInfo.summary.totalChanges;
      notify(`Saved ${savedCount} change${savedCount !== 1 ? 's' : ''}`);
    } catch (error) {
      const saveErrorData = buildSaveErrorData(error);
      console.error('[Forge][Master Label] Save failed', {
        error,
        saveErrorData,
        debugSummary: debugInfo?.summary,
        request: debugInfo?.request,
      });
      if (debugGroupOpen) console.groupEnd();
      if (showSaveReview && debugInfo) {
        setSaveReviewData({
          debugInfo,
          error: saveErrorData,
        });
      }
      if (error instanceof ForgeApiError && error.status === 409) {
        const dirtyUpcs = [...editor.diffSummary.dirtyItemNumbers];
        try {
          const freshData = await Promise.all(
            dirtyUpcs.map((itemNumber) => ForgeApiService.fetchItem(token, itemNumber, selectedChainId).then(transformApiUPCData))
          );
          editor.rebaseUpcs(freshData);
        } catch {
          // Leave local state intact if the conflict refresh fails.
        }
        notify(error.detail || 'Snapshot conflict — reloaded affected UPCs', 'error');
        setSaveError(error.detail || 'Snapshot conflict');
      } else {
        const message = saveErrorData.detail || saveErrorData.message || 'Save failed — changes preserved for retry';
        setSaveError(message);
        notify(message, 'error');
      }
    } finally {
      setIsSaving(false);
    }
  }, [token, isSaving, changeCount, selectedChainId, editor, notify, showSaveReview]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden', minWidth: 0, position: 'relative' }}>
      {/* Top item-entry bar */}
      <div style={{
        padding: '8px 12px',
        borderBottom: '1px solid var(--border)',
        flexShrink: 0,
        background: 'var(--surface)',
        display: 'flex', flexDirection: 'column', gap: 6,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {/* Chain selector */}
          <select
            value={selectedChainId}
            onChange={(e) => setSelectedChainId(Number(e.target.value))}
            style={{
              appearance: 'none',
              WebkitAppearance: 'none',
              background: `var(--bg) url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 24 24' fill='none' stroke='%23888888' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E") no-repeat right 6px center`,
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-sm)',
              padding: '5px 24px 5px 8px',
              color: 'var(--text-primary)',
              fontFamily: 'var(--font-mono)',
              fontSize: 11,
              fontWeight: 600,
              flexShrink: 0,
              cursor: 'pointer',
              outline: 'none',
            }}
            onFocus={(e) => { e.currentTarget.style.borderColor = 'var(--accent-border)'; }}
            onBlur={(e) => { e.currentTarget.style.borderColor = 'var(--border)'; }}
          >
            {(hasInternalAccess ? CHAIN_OPTIONS : CHAIN_OPTIONS.filter(c => c.id === 46)).map((c) => (
              <option key={c.id} value={c.id}>{c.label}</option>
            ))}
          </select>

          {/* Item-number input */}
          <div style={{ position: 'relative', flex: 1, maxWidth: 420 }}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 6,
              background: 'var(--bg)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-sm)',
              padding: '4px 8px',
              borderColor: searchFocused ? 'var(--accent-border)' : 'var(--border)',
              transition: 'border-color 150ms ease',
            }}>
              <SearchOutlinedIcon style={{ fontSize: 14, color: 'var(--text-tertiary)', flexShrink: 0 }} />
              <input
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                onFocus={() => setSearchFocused(true)}
                onBlur={() => setSearchFocused(false)}
                onKeyDown={handleSearchKeyDown}
                placeholder="Enter full item_number and press Enter"
                style={{
                  background: 'none', border: 'none', outline: 'none',
                  color: 'var(--text-primary)', fontFamily: 'var(--font-mono)',
                  fontSize: 11, width: '100%',
                }}
              />
            </div>

          </div>

          {/* Crop size slider + image controls + zoom toggle */}
          {selectedItemNumbers.length > 0 && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0,
              marginLeft: 'auto', paddingLeft: 8,
            }}>
              <span style={{ fontSize: 9, color: 'var(--text-tertiary)', fontFamily: 'var(--font-mono)', whiteSpace: 'nowrap' }}>
                {textScale}%
              </span>
              <input
                type="range"
                min={50}
                max={200}
                value={textScale}
                onChange={e => setTextScale(Number(e.target.value))}
                style={{ width: 80, accentColor: 'var(--accent)' }}
              />
              <span style={{ fontSize: 9, color: 'var(--text-tertiary)', fontFamily: 'var(--font-mono)', whiteSpace: 'nowrap' }}>
                {cropSize}px
              </span>
              <input
                type="range"
                min={16}
                max={260}
                value={cropSize}
                onChange={e => setCropSize(Number(e.target.value))}
                style={{ width: 80, accentColor: 'var(--accent)' }}
              />
              <button
                onClick={handleLoadRemainingImages}
                disabled={isLoadingRemainingImages || imageCacheSummary.total === 0 || (imageCacheSummary.failed === 0 && imageCacheSummary.idle === 0)}
                title="Retry any crop images that are still not loaded"
                style={{
                  fontSize: 9, fontFamily: 'var(--font-mono)', fontWeight: 600,
                  padding: '2px 6px', borderRadius: 'var(--radius-sm)', cursor: 'pointer',
                  border: '1px solid var(--border)',
                  background: 'var(--bg)',
                  color: 'var(--text-primary)',
                  display: 'flex', alignItems: 'center', gap: 3,
                  transition: 'all 150ms',
                  opacity: isLoadingRemainingImages || imageCacheSummary.total === 0 || (imageCacheSummary.failed === 0 && imageCacheSummary.idle === 0) ? 0.45 : 1,
                }}
              >
                {isLoadingRemainingImages ? (
                  <CircularProgress size={11} style={{ color: 'var(--text-tertiary)' }} />
                ) : (
                  <CloudDownloadOutlinedIcon style={{ fontSize: 11 }} />
                )}
                Load Remaining
              </button>
              <span style={{ fontSize: 9, color: 'var(--text-tertiary)', fontFamily: 'var(--font-mono)', whiteSpace: 'nowrap' }}>
                {imageCacheSummary.loaded}/{imageCacheSummary.total}
              </span>
              <button
                onClick={() => setAutoReloadImages(prev => !prev)}
                title={autoReloadImages ? 'Auto-reload missing crop images (ON)' : 'Auto-reload missing crop images (OFF)'}
                style={{
                  fontSize: 9, fontFamily: 'var(--font-mono)', fontWeight: 600,
                  padding: '2px 6px', borderRadius: 'var(--radius-sm)', cursor: 'pointer',
                  border: autoReloadImages ? '1px solid var(--accent-border)' : '1px solid var(--border)',
                  background: autoReloadImages ? 'var(--accent-subtle)' : 'var(--bg)',
                  color: autoReloadImages ? 'var(--accent)' : 'var(--text-tertiary)',
                  display: 'flex', alignItems: 'center', gap: 3,
                  transition: 'all 150ms',
                }}
              >
                <AutorenewOutlinedIcon style={{ fontSize: 11 }} /> Auto
              </button>
              <div style={{ width: 1, height: 14, background: 'var(--border)', flexShrink: 0 }} />
              <button
                onClick={toggleZoom}
                title={zoomEnabled ? 'Zoom on hover (ON)' : 'Zoom on hover (OFF — hold Alt)'}
                style={{
                  fontSize: 9, fontFamily: 'var(--font-mono)', fontWeight: 600,
                  padding: '2px 6px', borderRadius: 'var(--radius-sm)', cursor: 'pointer',
                  border: zoomEnabled ? '1px solid var(--purple-border)' : '1px solid var(--border)',
                  background: zoomEnabled ? 'var(--purple-subtle)' : 'var(--bg)',
                  color: zoomEnabled ? 'var(--purple)' : 'var(--text-tertiary)',
                  display: 'flex', alignItems: 'center', gap: 3,
                  transition: 'all 150ms',
                }}
              >
                <ZoomInIcon style={{ fontSize: 11 }} /> Zoom
              </button>
            </div>
          )}

          {/* Loaded UPC chips */}
          {selectedItemNumbers.map((itemNumber) => {
            const data = editor.upcData.find((entry) => entry.itemNumber === itemNumber);
            return (
              <div key={itemNumber} style={{
                display: 'flex', alignItems: 'center', gap: 4,
                background: 'var(--accent-subtle)',
                border: '1px solid var(--accent-border)',
                borderRadius: 10, padding: '2px 6px 2px 8px',
                fontFamily: 'var(--font-mono)', fontSize: 10,
                color: 'var(--accent)', fontWeight: 600,
                flexShrink: 0,
              }}>
                {data?.itemNumber || itemNumber}
                <CloseIcon
                  onClick={() => removeUPC(itemNumber)}
                  style={{ fontSize: 12, cursor: 'pointer', opacity: 0.7 }}
                />
              </div>
            );
          })}
        </div>
      </div>

      {/* Main content */}
      {selectedItemNumbers.length === 0 && !deepLinkLoading && !pendingColumn ? (
        <div style={{
          flex: 1, display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center', gap: 12,
          color: 'var(--text-tertiary)',
        }}>
          <div style={{ fontSize: 14, fontWeight: 600 }}>No items selected</div>
          <div style={{ fontSize: 12, maxWidth: 320, textAlign: 'center', lineHeight: 1.5 }}>
            Enter a full item_number above and press Enter to load one or more item columns.
          </div>
        </div>
      ) : selectedItemNumbers.length === 0 && deepLinkLoading && !pendingColumn ? (
        <div style={{
          flex: 1, display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center', gap: 12,
          color: 'var(--text-tertiary)',
        }}>
          <CircularProgress size={28} style={{ color: 'var(--text-tertiary)' }} />
          <div style={{ fontSize: 12, fontWeight: 600 }}>Loading items...</div>
        </div>
      ) : (
        <div ref={contentRef} style={{
          flex: 1, display: 'flex', gap: 10, padding: '8px 12px',
          overflowX: 'auto', overflowY: 'hidden', alignItems: 'stretch',
          minWidth: 0, minHeight: 0,
        }}>
          {pendingColumn && (
            <div style={{
              width: 420,
              minWidth: 420,
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius)',
              background: 'var(--surface)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 10,
              color: 'var(--text-tertiary)',
              padding: '24px 16px',
            }}>
              <CircularProgress size={22} style={{ color: 'var(--text-tertiary)' }} />
              <div style={{ fontSize: 11, fontFamily: 'var(--font-mono)', fontWeight: 600 }}>
                {`Loading item ${pendingColumn.label}`}
              </div>
              <div style={{ fontSize: 10, fontFamily: 'var(--font-mono)' }}>
                Preparing item column...
              </div>
            </div>
          )}
          {editor.upcData.map(upc => (
            <UPCColumnView
              key={upc.itemNumber}
              upc={upc}
              selectedCropIds={editor.selectedCropIds}
              highlightedCropIds={highlightCropIds}
              highlightColor={highlightPreset?.color}
              highlightGlow={highlightPreset?.glow}
              onCropClick={editor.toggleCropSelection}
              onSectionClick={(target) => {
                if (target.type === 'inactive' && selectedHasScanSource) { notify('Cannot deactivate scanned crops', 'warning'); return; }
                editor.handleSectionClick(target);
              }}
              mode={editor.mode}
              mergeSourceDesignId={null}
              activeDesignId={editor.activeDesignId}
              onActiveDesignChange={editor.setActiveDesignId}
              designColorMap={editor.designColorMap}
              onImageHover={handleImageHover}
              onImageMove={handleImageMove}
              onImageLeave={handleImageLeave}
              onRemoveUpc={removeUPC}
              recentlyMovedCropIds={editor.recentlyMovedCropIds}
              cropSize={cropSize}
              textScale={textScale}
              reloadVersion={reloadVersion}
              onPreloadGroup={preloadImages}
            />
          ))}
        </div>
      )}

      {/* Bottom action bar — always visible when items are loaded */}
      {selectedItemNumbers.length > 0 && (
        <CropActionBar
          mode={editor.mode}
          selectedCropIds={editor.selectedCropIds}
          newDesignOptions={newDesignOptions}
          onDeactivate={editor.deactivateCrops}
          onSetRep={editor.selectedCropDesignInfo && !editor.selectedCropDesignInfo.isAlreadyRep ? editor.selectedCropDesignInfo : null}
          onSetRepClick={editor.setRepresentative}
          onNewDesignClick={editor.createNewDesign}
          undoCount={editor.undoStack.length}
          onUndo={editor.undo}
          idleContent={
            highlightPreset && highlightCropIds.size > 0
              ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                  <span style={{
                    color: highlightPreset.color,
                    fontFamily: 'var(--font-mono)',
                    fontSize: 10,
                    fontWeight: 600,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}>
                    {highlightPreset.message}
                  </span>
                  {changeCount > 0 && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                      <button
                        onClick={handleSubmit}
                        disabled={isSaving}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 5,
                          padding: '4px 14px',
                          background: isSaving ? 'var(--text-tertiary)' : 'var(--purple)', border: 'none',
                          borderRadius: 'var(--radius-sm)',
                          color: '#fff', fontSize: 11, fontWeight: 600,
                          cursor: isSaving ? 'not-allowed' : 'pointer', fontFamily: 'var(--font-mono)',
                          opacity: isSaving ? 0.7 : 1,
                          flexShrink: 0,
                        }}
                      >
                        {isSaving ? (
                          <CircularProgress size={13} style={{ color: '#fff' }} />
                        ) : (
                          <CheckCircleOutlineIcon style={{ fontSize: 13 }} />
                        )}
                        {isSaving ? 'Saving...' : `Save ${changeCount} change${changeCount !== 1 ? 's' : ''}`}
                      </button>
                      <label
                        title={showSaveReview ? 'Save review popup enabled' : 'Save review popup disabled'}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 4,
                          fontSize: 9,
                          fontFamily: 'var(--font-mono)',
                          color: showSaveReview ? 'var(--text-primary)' : 'var(--text-tertiary)',
                          cursor: 'pointer',
                          userSelect: 'none',
                        }}
                      >
                        <input
                          type="checkbox"
                          className="forge-checkbox"
                          checked={showSaveReview}
                          onChange={(event) => setShowSaveReview(event.target.checked)}
                        />
                        Save Review
                      </label>
                    </div>
                  )}
                  {saveError && (
                    <span style={{ fontSize: 10, color: 'var(--red)', fontFamily: 'var(--font-mono)', flexShrink: 0 }}>
                      {saveError}
                    </span>
                  )}
                </div>
              )
              : changeCount > 0 ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <button
                    onClick={handleSubmit}
                    disabled={isSaving}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 5,
                      padding: '4px 14px',
                      background: isSaving ? 'var(--text-tertiary)' : 'var(--purple)', border: 'none',
                      borderRadius: 'var(--radius-sm)',
                      color: '#fff', fontSize: 11, fontWeight: 600,
                      cursor: isSaving ? 'not-allowed' : 'pointer', fontFamily: 'var(--font-mono)',
                      opacity: isSaving ? 0.7 : 1,
                    }}
                  >
                    {isSaving ? (
                      <CircularProgress size={13} style={{ color: '#fff' }} />
                    ) : (
                      <CheckCircleOutlineIcon style={{ fontSize: 13 }} />
                    )}
                    {isSaving ? 'Saving...' : `Save ${changeCount} change${changeCount !== 1 ? 's' : ''}`}
                  </button>
                  <label
                    title={showSaveReview ? 'Save review popup enabled' : 'Save review popup disabled'}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4,
                      fontSize: 9,
                      fontFamily: 'var(--font-mono)',
                      color: showSaveReview ? 'var(--text-primary)' : 'var(--text-tertiary)',
                      cursor: 'pointer',
                      userSelect: 'none',
                    }}
                  >
                    <input
                      type="checkbox"
                      className="forge-checkbox"
                      checked={showSaveReview}
                      onChange={(event) => setShowSaveReview(event.target.checked)}
                    />
                    Save Review
                  </label>
                  {saveError && (
                    <span style={{ fontSize: 10, color: 'var(--red)', fontFamily: 'var(--font-mono)' }}>
                      {saveError}
                    </span>
                  )}
                </div>
              ) : undefined
          }
        />
      )}

      {ZoomOverlay}

      {saveReviewData && (
        <SaveReviewDialog
          data={saveReviewData}
          enabled={showSaveReview}
          onEnabledChange={setShowSaveReview}
          onClose={() => setSaveReviewData(null)}
        />
      )}

      {NotificationToast}
    </div>
  );
};
